import { useEffect, useState, type RefObject } from "react";
import { ChevronDown } from "lucide-react";

interface NavScrollIndicatorProps {
  /** Container com overflow-y:auto que contem os itens do menu (`.nav__scroll`). */
  scrollRef: RefObject<HTMLDivElement>;
  /** Recalcula quando o numero de itens do menu muda (ex: canais carregam). */
  itemCount: number;
}

/**
 * Isola a MEDICAO do indicador de rolagem do menu (R6 do plano de redesign):
 * a barra nativa e ocultada (.scroll-silent no container), entao e obrigatorio
 * o degrade no topo/rodape mais o contador clicavel "+N v" com o numero REAL
 * de itens fora da area visivel — nunca um numero fixo. Menu com todos os
 * itens visiveis nao mostra nada.
 */
export function NavScrollIndicator({ scrollRef, itemCount }: NavScrollIndicatorProps): JSX.Element | null {
  const [showTop, setShowTop] = useState(false);
  const [hiddenBelow, setHiddenBelow] = useState(0);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const measure = (): void => {
      const { scrollTop, clientHeight } = el;
      setShowTop(scrollTop > 1);

      const visibleBottom = scrollTop + clientHeight;
      const items = el.querySelectorAll<HTMLElement>(".nav__item");
      let hidden = 0;
      items.forEach((item) => {
        // Item conta como "fora da area visivel" quando o seu topo ja passou
        // do fundo visivel do container (medido em relacao ao proprio scroll,
        // nao ao viewport — funciona igual com o menu recolhido ou nao).
        if (item.offsetTop >= visibleBottom - 1) hidden += 1;
      });
      setHiddenBelow(hidden);
    };

    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", measure);
      ro.disconnect();
    };
  }, [scrollRef, itemCount]);

  const handleMore = (): void => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ top: el.clientHeight * 0.8, behavior: "smooth" });
  };

  return (
    <>
      {showTop ? <div className="nav__fade-top" aria-hidden /> : null}
      {hiddenBelow > 0 ? (
        <>
          <div className="nav__fade-bottom" aria-hidden />
          <button
            type="button"
            className="nav__more"
            onClick={handleMore}
            aria-label={`Mostrar mais ${hiddenBelow} itens do menu`}
          >
            <span>+{hiddenBelow}</span>
            <ChevronDown className="icon" aria-hidden style={{ width: "0.875rem", height: "0.875rem" }} />
          </button>
        </>
      ) : null}
    </>
  );
}
